/**
 * §10 `ClauseBreaker` — the ONE reader of the break-preference shorthand (S10 C4).
 *
 * Netflix's line-treatment rule is *"break after punctuation marks, before conjunctions,
 * before prepositions"*, so a break-AFTER-only vocabulary cannot express half of a standard
 * this product cites. CLEAN shipped an interim convention on the data — a leading space means
 * "before" — carried by the implementation CONVERT imports, so the two could not drift.
 * Contract 1.4.0 promotes that into the type as an explicit `{ text, side }` form while
 * keeping the shorthand.
 *
 * WHY THE SHORTHAND SURVIVES rather than being migrated away: CLEAN's shipped default list is
 * written that way, it is the default in every preset the product will ship with, and
 * reinterpreting ' and' as break-AFTER-" and" would silently move every break in the product.
 * A shape change that is invisible in the diff and visible in the output is the worst kind.
 *
 * This function is the only place the shorthand is interpreted. Do not re-read the list by
 * hand; that is how two interpretations appear.
 */

import type { ClauseBreaker } from '../../../contracts/cue-model.ts';

export interface NormalizedClauseBreaker {
  /** The token, with the signalling whitespace removed and never empty. */
  readonly text: string;
  readonly side: 'before' | 'after';
}

/**
 * Resolves either form to `{ text, side }`.
 *
 * `{ text: 'and', side: 'before' }`  → break before "and"
 * `' and'`                           → break before "and"   (leading space = the shorthand)
 * `'. '`                             → break after "."      (trailing space is not signalling)
 * `'。'`                             → break after "。"
 *
 * An entry that is empty, or is only whitespace, normalizes to `text: ''` — callers skip
 * those rather than matching every position in the line.
 */
export function normalizeClauseBreaker(b: ClauseBreaker): NormalizedClauseBreaker {
  if (typeof b !== 'string') {
    return { text: b.text.trim(), side: b.side };
  }
  // A LEADING space is the signal. A trailing one is not: '. ' means "after a full stop", and
  // the space is there because the list is written the way the text reads.
  if (/^\s/.test(b)) return { text: b.trimStart().trim(), side: 'before' };
  return { text: b.trimEnd(), side: 'after' };
}

/** Normalizes a whole list and drops the empty entries. Rank is preserved as the index. */
export function normalizeClauseBreakers(
  list: readonly ClauseBreaker[],
): readonly NormalizedClauseBreaker[] {
  const out: NormalizedClauseBreaker[] = [];
  for (const b of list) {
    const n = normalizeClauseBreaker(b);
    if (n.text !== '') out.push(n);
  }
  return out;
}
